import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useInventory } from '../context/InventoryContext';
import { FaFileExcel, FaFilePdf, FaChevronLeft, FaChevronRight, FaSearch } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import api from "../services/api";

const Reports = () => {
  // Extracted extra user details needed for the updated backend route parameters
  const { user, currentUser, currentCompanyId } = useAuth() || {};
  
  const userRole = user?.role || user?.type || "super_admin";
  
  // Get inventory context for devices, sims, and vehicles
  const { devices, sims, vehicles } = useInventory() || {};
  
  const [queriesList, setQueriesList] = useState([]);
  const [dateRange, setDateRange] = useState('7days');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filteredQueries, setFilteredQueries] = useState([]);

  // Filter states
  const [selectedInstaller, setSelectedInstaller] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  
  // Single unified search field
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // Check if user is Super Admin
  const isSuperAdmin = userRole === 'super_admin';

  // Helper functions to get device and SIM details
  const getDeviceImei = (deviceId) => {
    if (!deviceId) return '-';
    const device = devices?.find(d => String(d.id) === String(deviceId));
    return device?.imei || device?.slNumber || '-';
  };

  const getSimNo = (simId) => {
    if (!simId) return '-';
    const sim = sims?.find(s => String(s.id) === String(simId));
    return sim?.simNo || '-';
  };

  const getMobileNo = (simId) => {
    if (!simId) return '-';
    const sim = sims?.find(s => String(s.id) === String(simId));
    return sim?.mobileNo || '-';
  };

  // Helper function to format date from API to DD-MM-YYYY - HH:MM
  const formatDateToLocal = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}-${month}-${year} - ${hours}:${minutes}`;
    } catch (e) {
      return dateString;
    }
  };

  // Helper function to render tooltip content for multi-value cells
  const renderTooltipCell = (items, maxDisplay = 2) => {
    if (!items || items === 'N/A' || items === '-') {
      return <span className="text-gray-400">-</span>;
    }

    // Split by comma if it's a string
    let itemArray = [];
    if (typeof items === 'string') {
      itemArray = items.split(',').map(v => v.trim()).filter(v => v && v !== '');
    } else if (Array.isArray(items)) {
      itemArray = items.filter(v => v && v !== '');
    } else {
      return <span>{items}</span>;
    }

    if (itemArray.length === 0) {
      return <span className="text-gray-400">-</span>;
    }

    if (itemArray.length === 1) {
      return <span>{itemArray[0]}</span>;
    }

    const displayItems = itemArray.slice(0, maxDisplay);
    const remainingItems = itemArray.slice(maxDisplay);
    const hasMore = remainingItems.length > 0;
    const tooltipText = itemArray.join(', ');

    return (
      <div className="relative group inline-block">
        <span className="cursor-help">
          {displayItems.join(', ')}
          {hasMore && (
            <span className="text-gray-400 text-xs"> +{remainingItems.length} more</span>
          )}
        </span>
        {itemArray.length > maxDisplay && (
          <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 bg-gray-900 text-white text-xs rounded-lg p-3 whitespace-normal min-w-[200px] max-w-[350px] shadow-xl">
            {tooltipText}
            <div className="absolute left-4 top-full -mt-1 border-4 border-transparent border-t-gray-900"></div>
          </div>
        )}
      </div>
    );
  };

  // LOAD QUERIES MATCHING BACKEND ROUTE PARAMETERS
  const loadReports = useCallback(async () => {
    if (!userRole) return;
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || "https://cccs.cordonnx.com";
      const activeUsername = currentUser?.username || currentUser?.name || '';
      
      const response = await fetch(
        `${API_BASE_URL}/api/queries?role=${userRole}&companyId=${currentCompanyId}&username=${encodeURIComponent(activeUsername)}`
      );
      const data = await response.json();
      
      if (Array.isArray(data)) {
        const formattedData = data.map(log => {
          // Parse vehicle IDs from vehicle_ids field
          let vehicleIds = [];
          
          if (log.vehicle_ids) {
            try {
              if (typeof log.vehicle_ids === 'string') {
                vehicleIds = JSON.parse(log.vehicle_ids);
              } else if (Array.isArray(log.vehicle_ids)) {
                vehicleIds = log.vehicle_ids;
              }
            } catch (e) {
              console.error('Error parsing vehicle_ids:', e);
            }
          }
          
          return {
            id: log.id,
            ticketNo: log.ticket_no || log.ticketNo,
            companyName: log.company_name || log.companyName,
            subCompanyName: log.sub_company_name || log.subCompanyName,
            installerName: log.installer_name || log.installerName,
            vehicleName: log.vehicle_name || log.vehicleName || log.vehicles,
            vehicleIds: vehicleIds,
            queryType: log.query_type || log.queryType,
            description: log.description,
            status: log.status,
            fcr_numbers: log.fcr_numbers,
            createdAt: log.created_at || log.createdAt,
            updatedAt: log.updated_at || log.updatedAt
          };
        });
        
        setQueriesList(formattedData);
      }
    } catch (error) {
      console.error('Error fetching data from PostgreSQL Backend:', error);
    }
  }, [userRole, currentCompanyId, currentUser]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  // Helper function to safely parse your custom format "DD-MM-YYYY - HH:MM" into a valid JS Date
  const parseCustomTimestamp = (timestampStr) => {
    if (!timestampStr) return new Date(0);
    try {
      const mainParts = timestampStr.split(' - ');
      if (mainParts.length < 2) return new Date(timestampStr);

      const dateParts = mainParts[0].split('-'); 
      const timeParts = mainParts[1].split(':'); 

      const day = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1; 
      const year = parseInt(dateParts[2], 10);
      const hours = parseInt(timeParts[0], 10);
      const minutes = parseInt(timeParts[1], 10);

      return new Date(year, month, day, hours, minutes);
    } catch (err) {
      return new Date(timestampStr);
    }
  };

  // Get unique installers and companies for dropdown options
  const getUniqueInstallers = () => {
    const installers = queriesList
      .map(q => q.installerName)
      .filter(name => name && name.trim() !== '');
    return [...new Set(installers)].sort();
  };

  const getUniqueCompanies = () => {
    const companies = queriesList
      .map(q => q.companyName)
      .filter(name => name && name.trim() !== '');
    return [...new Set(companies)].sort();
  };

  // Function to get vehicle details with IMEI, SIM, Mobile from vehicles data
  const getVehicleDetails = (query) => {
    let imeiList = [];
    let simList = [];
    let mobileList = [];
    let vehicleNameList = [];
    
    if (query.vehicleIds && query.vehicleIds.length > 0) {
      // Get vehicle details from vehicles array
      query.vehicleIds.forEach((vehicleId) => {
        const vehicle = vehicles?.find(v => String(v.id) === String(vehicleId));
        if (vehicle) {
          // Get IMEI from device_id
          const imei = getDeviceImei(vehicle.imei || vehicle.device_id);
          // Get SIM and Mobile from sim_id
          const simNo = getSimNo(vehicle.sim || vehicle.sim_id);
          const mobileNo = getMobileNo(vehicle.sim || vehicle.sim_id);
          
          imeiList.push(imei);
          simList.push(simNo);
          mobileList.push(mobileNo);
          vehicleNameList.push(vehicle.licensePlate || `Vehicle ${vehicleId}`);
        } else {
          // If vehicle not found in vehicles array, try to get from query
          imeiList.push('-');
          simList.push('-');
          mobileList.push('-');
          vehicleNameList.push(`Vehicle ${vehicleId}`);
        }
      });
    } else if (query.vehicleName) {
      // If no vehicle IDs but vehicle name exists, try to find by license plate
      const vehicleNames = query.vehicleName.split(',').map(v => v.trim());
      vehicleNames.forEach((name) => {
        const vehicle = vehicles?.find(v => v.licensePlate === name);
        if (vehicle) {
          const imei = getDeviceImei(vehicle.imei || vehicle.device_id);
          const simNo = getSimNo(vehicle.sim || vehicle.sim_id);
          const mobileNo = getMobileNo(vehicle.sim || vehicle.sim_id);
          
          imeiList.push(imei);
          simList.push(simNo);
          mobileList.push(mobileNo);
          vehicleNameList.push(vehicle.licensePlate);
        } else {
          imeiList.push('-');
          simList.push('-');
          mobileList.push('-');
          vehicleNameList.push(name);
        }
      });
    }
    
    // Join lists with comma
    const vehicleNamesStr = vehicleNameList.join(', ') || query.vehicleName || 'N/A';
    const imeiListStr = imeiList.join(', ') || 'N/A';
    const simListStr = simList.join(', ') || 'N/A';
    const mobileListStr = mobileList.join(', ') || 'N/A';
    
    return {
      vehicleNames: vehicleNamesStr,
      vehicleNamesArray: vehicleNameList,
      imeiList: imeiListStr,
      imeiListArray: imeiList,
      simList: simListStr,
      simListArray: simList,
      mobileList: mobileListStr,
      mobileListArray: mobileList
    };
  };

  // Filter and sort queries based on selection criteria
  useEffect(() => {
    let filtered = [...queriesList];
    
    // Sort logic
    filtered.sort((a, b) => {
      const timeA = parseCustomTimestamp(a.updatedAt || a.createdAt).getTime();
      const timeB = parseCustomTimestamp(b.updatedAt || b.createdAt).getTime();
      return timeB - timeA; 
    });
    
    // Filter logic
    const now = new Date();
    let startDateObj = null;
    let endDateObj = new Date();
    
    if (dateRange === '7days') {
      startDateObj = new Date();
      startDateObj.setDate(now.getDate() - 7);
    } else if (dateRange === '15days') {
      startDateObj = new Date();
      startDateObj.setDate(now.getDate() - 15);
    } else if (dateRange === '1month') {
      startDateObj = new Date();
      startDateObj.setMonth(now.getMonth() - 1);
    } else if (dateRange === 'calendar' && startDate && endDate) {
      startDateObj = new Date(startDate);
      startDateObj.setHours(0, 0, 0, 0);
      endDateObj = new Date(endDate);
      endDateObj.setHours(23, 59, 59, 999);
    }
    
    if (startDateObj) {
      filtered = filtered.filter(q => {
        const queryDate = parseCustomTimestamp(q.createdAt);
        if (dateRange === 'calendar') {
          return queryDate >= startDateObj && queryDate <= endDateObj;
        }
        return queryDate >= startDateObj;
      });
    }

    // Filter by Installer
    if (selectedInstaller) {
      filtered = filtered.filter(q => q.installerName === selectedInstaller);
    }

    // Filter by Company
    if (selectedCompany) {
      filtered = filtered.filter(q => q.companyName === selectedCompany);
    }

    // 🔍 UNIFIED SEARCH: Search across Vehicle Number, IMEI, SIM No, and Mobile No
    if (searchQuery.trim()) {
      const searchTerm = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(q => {
        const details = getVehicleDetails(q);
        
        // Search in Vehicle Names
        const vehicleMatch = details.vehicleNamesArray.some(v => 
          v.toLowerCase().includes(searchTerm)
        ) || details.vehicleNames.toLowerCase().includes(searchTerm);
        
        // Search in IMEI
        const imeiMatch = details.imeiListArray.some(v => 
          v.toLowerCase().includes(searchTerm)
        ) || details.imeiList.toLowerCase().includes(searchTerm);
        
        // Search in SIM
        const simMatch = details.simListArray.some(v => 
          v.toLowerCase().includes(searchTerm)
        ) || details.simList.toLowerCase().includes(searchTerm);
        
        // Search in Mobile
        const mobileMatch = details.mobileListArray.some(v => 
          v.toLowerCase().includes(searchTerm)
        ) || details.mobileList.toLowerCase().includes(searchTerm);
        
        return vehicleMatch || imeiMatch || simMatch || mobileMatch;
      });
    }
    
    setFilteredQueries(filtered);
    setCurrentPage(1); // Reset page selection whenever active filters change
  }, [queriesList, dateRange, startDate, endDate, selectedInstaller, selectedCompany, searchQuery]);

  // Pagination Math calculations
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentTableRows = filteredQueries.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredQueries.length / rowsPerPage);

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(prev => prev - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
  };

  // Export to Excel Handler
  const exportToExcel = () => {
    if (filteredQueries.length === 0) {
      alert("No data available to export!");
      return;
    }

    const exportData = filteredQueries.map(q => {
      const details = getVehicleDetails(q);
      
      return {
        'Ticket ID': q.ticketNo,
        'Company Name': q.companyName,
        'Sub Company': q.subCompanyName || 'N/A',
        'Vehicle(s)': details.vehicleNames,
        'IMEI': details.imeiList,
        'SIM No': details.simList,
        'Mobile No': details.mobileList,
        'Installer': q.installerName || 'N/A',
        'Issue Category': q.queryType,
        'Description': q.description || '',
        'Status': q.status,
        'FCR Numbers': (() => {
          if (!q.fcr_numbers) return 'N/A';
          try {
            const fcrData = typeof q.fcr_numbers === 'string' 
              ? JSON.parse(q.fcr_numbers) 
              : q.fcr_numbers;
            const fcrValues = Object.values(fcrData).filter(v => v && v !== '');
            return fcrValues.length > 0 ? fcrValues.join(', ') : 'N/A';
          } catch (e) {
            return 'N/A';
          }
        })(),
        'Created Time': formatDateToLocal(q.createdAt),
        'Updated Time': formatDateToLocal(q.updatedAt || q.createdAt)
      };
    });
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    ws['!cols'] = [
      { wch: 15 }, { wch: 25 }, { wch: 20 }, { wch: 25 }, { wch: 20 },
      { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 25 }, { wch: 40 },
      { wch: 15 }, { wch: 20 }, { wch: 22 }, { wch: 22 }
    ];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Query Reports');
    
    const fileName = `query_report_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Export to PDF Handler
  const exportToPDF = () => {
    if (filteredQueries.length === 0) {
      alert("No data available to export!");
      return;
    }

    const doc = new jsPDF('l', 'mm', 'a4');
    
    doc.setFontSize(18);
    doc.text('Query Reports Log Ledger', 14, 18);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 26);
    doc.text(`Date Range Filter: ${getDateRangeText()}`, 14, 32);
    doc.text(`Total Document Records: ${filteredQueries.length}`, 14, 38);
    
    const tableData = filteredQueries.map(q => {
      const details = getVehicleDetails(q);
      
      return [
        q.ticketNo,
        q.companyName,
        q.subCompanyName || 'N/A',
        details.vehicleNames,
        details.imeiList,
        details.simList,
        details.mobileList,
        q.installerName || 'N/A',
        q.queryType,
        q.status,
        (() => {
          if (!q.fcr_numbers) return 'N/A';
          try {
            const fcrData = typeof q.fcr_numbers === 'string' 
              ? JSON.parse(q.fcr_numbers) 
              : q.fcr_numbers;
            const fcrValues = Object.values(fcrData).filter(v => v && v !== '');
            return fcrValues.length > 0 ? fcrValues.join(', ') : 'N/A';
          } catch (e) {
            return 'N/A';
          }
        })(),
        formatDateToLocal(q.createdAt),
        formatDateToLocal(q.updatedAt || q.createdAt)
      ];
    });
    
    doc.autoTable({
      startY: 44,
      head: [['Ticket ID', 'Company', 'Sub Company', 'Vehicle(s)', 'IMEI', 'SIM No', 'Mobile No', 'Installer', 'Category', 'Status', 'FCR Numbers', 'Created Time', 'Updated Time']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [79, 70, 229], textColor: 255, fontSize: 8, fontStyle: 'bold' },
      styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
      columnStyles: {
        0: { cellWidth: 18 }, 1: { cellWidth: 22 }, 2: { cellWidth: 20 },
        3: { cellWidth: 22 }, 4: { cellWidth: 20 }, 5: { cellWidth: 18 },
        6: { cellWidth: 18 }, 7: { cellWidth: 20 }, 8: { cellWidth: 22 },
        9: { cellWidth: 16 }, 10: { cellWidth: 18 }, 11: { cellWidth: 25 }, 12: { cellWidth: 25 }
      }
    });
    
    doc.save(`query_report_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const getDateRangeText = () => {
    if (dateRange === '7days') return 'Last 7 days';
    if (dateRange === '15days') return 'Last 15 days';
    if (dateRange === '1month') return 'Last 1 month';
    if (dateRange === 'calendar') return `${startDate} to ${endDate}`;
    return 'All time';
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Pending': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'Completed': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-[60vh] bg-gray-50">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg max-w-md">
          <div className="text-red-500 text-6xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h1>
          <p className="text-gray-600">This page is only accessible to Super Admin users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto bg-gray-50 min-h-screen font-sans">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Query Reports Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">Download real-time logs reports in structured Excel or PDF tables</p>
      </div>

      {/* Control Filters Panel */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-base font-semibold text-gray-800 mb-4">Filter Activity Records</h2>
        
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Quick Select Range</label>
            <select
              value={dateRange}
              onChange={(e) => {
                setDateRange(e.target.value);
                if (e.target.value !== 'calendar') {
                  setStartDate('');
                  setEndDate('');
                }
              }}
              className="border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="7days">Last 7 Days</option>
              <option value="15days">Last 15 Days</option>
              <option value="1month">Last 1 Month</option>
              <option value="calendar">Custom Date Scope Range</option>
            </select>
          </div>

          {dateRange === 'calendar' && (
            <>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </>
          )}

          {/* Installer Filter - Dropdown */}
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Filter by Installer</label>
            <select
              value={selectedInstaller}
              onChange={(e) => setSelectedInstaller(e.target.value)}
              className="border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 min-w-[180px]"
            >
              <option value="">All Installers</option>
              {getUniqueInstallers().map((installer, index) => (
                <option key={index} value={installer}>
                  {installer}
                </option>
              ))}
            </select>
          </div>

          {/* Company Filter - Dropdown */}
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Filter by Company</label>
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 min-w-[180px]"
            >
              <option value="">All Companies</option>
              {getUniqueCompanies().map((company, index) => (
                <option key={index} value={company}>
                  {company}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 🔍 Unified Search Field - Searches across Vehicle, IMEI, SIM, Mobile */}
        <div className="flex flex-wrap gap-4 items-end mt-4 pt-4 border-t border-gray-100">
          <div className="flex-1 min-w-[300px]">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Search (Vehicle / IMEI / SIM / Mobile)</label>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="🔍 Search by Vehicle Number, IMEI, SIM No, or Mobile No..."
                className="w-full border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => setSearchQuery('')}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition whitespace-nowrap"
            >
              Clear Search
            </button>
            <button
              onClick={exportToExcel}
              disabled={filteredQueries.length === 0}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold shadow-sm transition ${
                filteredQueries.length === 0 
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }`}
            >
              <FaFileExcel /> Export Excel
            </button>
            <button
              onClick={exportToPDF}
              disabled={filteredQueries.length === 0}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold shadow-sm transition ${
                filteredQueries.length === 0 
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
            >
              <FaFilePdf /> Export PDF
            </button>
          </div>
        </div>

        {/* Search results info */}
        {searchQuery && (
          <div className="mt-2 text-xs text-indigo-600 font-medium">
            Showing results for: <span className="font-bold">"{searchQuery}"</span> ({filteredQueries.length} records found)
          </div>
        )}

        {/* Analytics Breakdown - Removed Ongoing */}
        <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-100">
          <div className="bg-indigo-50/60 border border-indigo-100 rounded-xl p-4">
            <p className="text-xs text-indigo-600 font-bold uppercase tracking-wider">Total Matches</p>
            <p className="text-2xl font-black text-indigo-900 mt-1">{filteredQueries.length}</p>
          </div>
          <div className="bg-amber-50/60 border border-amber-100 rounded-xl p-4">
            <p className="text-xs text-amber-600 font-bold uppercase tracking-wider">Pending</p>
            <p className="text-2xl font-black text-amber-900 mt-1">{filteredQueries.filter(q => q.status === 'Pending').length}</p>
          </div>
          <div className="bg-green-50/60 border border-green-100 rounded-xl p-4">
            <p className="text-xs text-green-600 font-bold uppercase tracking-wider">Completed</p>
            <p className="text-2xl font-black text-green-900 mt-1">{filteredQueries.filter(q => q.status === 'Completed').length}</p>
          </div>
        </div>
      </div>

      {/* Live Data Preview Grid Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50/50">
          <h2 className="text-base font-semibold text-gray-800">Query Details Preview (Newest Listed First)</h2>
          <span className="text-xs font-semibold bg-gray-200 text-gray-700 px-2.5 py-1 rounded-full">
            Records Found: {filteredQueries.length}
          </span>
        </div>
        
        {/* Table container with fixed height and scroll */}
        <div className="overflow-x-auto" style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-100 text-xs font-bold text-gray-500 uppercase border-b border-gray-200 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3">Ticket ID</th>
                <th className="px-4 py-3">Company Name</th>
                <th className="px-4 py-3">Sub Company</th>
                <th className="px-4 py-3">Vehicle(s)</th>
                <th className="px-4 py-3">IMEI</th>
                <th className="px-4 py-3">SIM No</th>
                <th className="px-4 py-3">Mobile No</th>
                <th className="px-4 py-3">Installer</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">FCR Numbers</th>
                <th className="px-4 py-3">Created Time</th>
                <th className="px-4 py-3">Updated Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 text-sm text-gray-700">
              {filteredQueries.length === 0 ? (
                <tr>
                  <td colSpan="13" className="px-4 py-12 text-center text-gray-400 font-medium italic">
                    {searchQuery 
                      ? `No matching records found for "${searchQuery}"` 
                      : 'No matching log ledgers found for the current selected parameters.'}
                  </td>
                </tr>
              ) : (
                currentTableRows.map((query) => {
                  const details = getVehicleDetails(query);
                  
                  return (
                    <tr key={query.ticketNo} className="hover:bg-gray-50/80 transition-colors">
                      <td className="px-4 py-3 font-mono text-xs font-bold text-blue-600 whitespace-nowrap">{query.ticketNo}</td>
                      <td className="px-4 py-3 font-semibold">{query.companyName}</td>
                      <td className="px-4 py-3 text-xs text-purple-700 font-medium">{query.subCompanyName || 'N/A'}</td>
                      <td className="px-4 py-3 text-xs text-blue-700 font-medium">
                        {renderTooltipCell(details.vehicleNamesArray.length > 0 ? details.vehicleNamesArray : details.vehicleNames)}
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-gray-600">
                        {renderTooltipCell(details.imeiListArray.length > 0 ? details.imeiListArray : details.imeiList)}
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-gray-600">
                        {renderTooltipCell(details.simListArray.length > 0 ? details.simListArray : details.simList)}
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-gray-600">
                        {renderTooltipCell(details.mobileListArray.length > 0 ? details.mobileListArray : details.mobileList)}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{query.installerName}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="px-2 py-0.5 bg-amber-50 text-amber-800 text-xs border border-amber-200 rounded-md font-medium">
                          {query.queryType}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full border ${getStatusBadgeClass(query.status)}`}>
                          {query.status}
                        </span>
                      </td>
                      {/* UPDATED: FCR Numbers column - Only first FCR visible with hover for more */}
                      <td className="px-4 py-3 text-xs font-mono font-bold">
                        {query.fcr_numbers ? (
                          (() => {
                            try {
                              const fcrData = typeof query.fcr_numbers === 'string' 
                                ? JSON.parse(query.fcr_numbers) 
                                : query.fcr_numbers;
                              const fcrValues = Object.values(fcrData).filter(v => v && v !== '');
                              if (fcrValues.length === 0) {
                                return <span className="text-gray-400">—</span>;
                              }
                              
                              // Show only first FCR number visible
                              const firstFCR = fcrValues[0];
                              const remainingCount = fcrValues.length - 1;
                              
                              return (
                                <div className="relative group inline-flex items-center gap-1">
                                  <span className="text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded text-[10px] font-mono cursor-help whitespace-nowrap">
                                    {firstFCR}
                                    {remainingCount > 0 && (
                                      <span className="text-gray-500 ml-1">+{remainingCount}</span>
                                    )}
                                  </span>
                                  
                                  {/* Hover tooltip showing all FCR numbers - only show if more than one */}
                                  {remainingCount > 0 && (
                                    <div className="absolute z-10 hidden group-hover:block bg-gray-800 text-white text-xs rounded-lg p-2 min-w-[200px] shadow-lg left-0 top-full mt-1">
                                      <div className="font-semibold mb-1">All FCR Numbers:</div>
                                      <div className="flex flex-wrap gap-1">
                                        {fcrValues.map((fcr, idx) => (
                                          <span key={idx} className="bg-emerald-700 text-emerald-100 px-2 py-0.5 rounded text-[10px] font-mono">
                                            {fcr}
                                          </span>
                                        ))}
                                      </div>
                                      <div className="text-gray-400 text-[10px] mt-1">Total: {fcrValues.length}</div>
                                    </div>
                                  )}
                                </div>
                              );
                            } catch (e) {
                              return <span className="text-gray-400">—</span>;
                            }
                          })()
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-gray-500 whitespace-nowrap">{formatDateToLocal(query.createdAt)}</td>
                      <td className="px-4 py-3 text-xs font-mono text-indigo-600 font-semibold whitespace-nowrap">{formatDateToLocal(query.updatedAt || query.createdAt)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Dynamic Pagination Footer Control Panel */}
        {filteredQueries.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center bg-gray-50/50 gap-4">
            <div className="text-xs text-gray-600 font-medium">
              Showing <span className="font-bold text-gray-900">{indexOfFirstRow + 1}</span> to{' '}
              <span className="font-bold text-gray-900">
                {indexOfLastRow > filteredQueries.length ? filteredQueries.length : indexOfLastRow}
              </span>{' '}
              of <span className="font-bold text-gray-900">{filteredQueries.length}</span> records
            </div>
            
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg border text-xs font-semibold shadow-sm transition flex items-center gap-1 ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'
                  }`}
                >
                  <FaChevronLeft className="w-3 h-3" /> Prev
                </button>
                
                <span className="text-xs font-bold text-gray-700 px-3 py-1 bg-gray-100 rounded-md border border-gray-200">
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg border text-xs font-semibold shadow-sm transition flex items-center gap-1 ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'
                  }`}
                >
                  Next <FaChevronRight className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;